import React, { useState, useEffect, useCallback } from 'react';
import styles from 'components/carousel/index.module.scss';
import classNames from 'classnames/bind';
import { useResize } from 'utils/resize-helper';
import iconNext from 'images/icon-next.svg';
import iconPrev from 'images/icon-prev.svg';
import iconNextDark from 'images/icon-next-dark.svg';
import iconPrevDark from 'images/icon-prev-dark.svg';
import blank from 'images/blank.png';
import { Spinners } from 'mochi-react-component';

const Carousel = ({
  breakpoint = [1023, 767],
  loading = true,
  items,
  fixedContainerWidth = [900, 600],
  darkMode = false,
  arrow = true,
  indicator = true,
  fullWidth = false,
  customWidth,
  onSwipe = () => {},
}) => {
  const cx = classNames.bind(styles);
  const deviceSize = useResize(breakpoint[0], breakpoint[1]);
  const [index, setIndex] = useState(0);
  const [count, setCount] = useState(1);
  const [pointStart, setPointStart] = useState([]);
  const [pointMove, setPointMove] = useState(0);
  const [isMoving, setIsMoving] = useState(false);
  const [direction, setDirection] = useState('');
  const [dragImage, setDragImage] = useState(undefined);

  useEffect(() => {
    const img = new Image();
    img.src = blank;
    setDragImage(img);
  }, []);

  //   moveing method --------v
  const preventDefault = useCallback(
    e => {
      if (isMoving) {
        e.preventDefault();
      }
    },
    [isMoving]
  );

  useEffect(() => {
    window.addEventListener('touchmove', preventDefault, { passive: false });
    return () => {
      window.removeEventListener('touchmove', preventDefault, { passive: false });
    };
  }, [isMoving, preventDefault]);

  // 透過 count 控制 UI
  useEffect(() => {
    if (deviceSize === 'desktop') {
      setCount(3);
    } else if (deviceSize === 'pad') {
      setCount(2);
    } else {
      setCount(1);
    }
  }, [deviceSize]);

  useEffect(() => {
    if (isMoving) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'inherit';
    }
  }, [isMoving]);

  const dragStart = e => {
    e.dataTransfer.setDragImage(dragImage, 1, 1);
    setPointStart([e.pageX, e.pageY]);
    setIsMoving(true);
  };

  const dragMove = e => {
    e.preventDefault();
    if (e.pageX !== 0 && isMoving) {
      const result = pointStart[0] - e.pageX;
      setPointMove(result);
    }
  };

  const dragEnd = e => {
    e.preventDefault();

    if (Math.abs(pointMove) > window.innerWidth / 5) {
      if (pointMove > 0) {
        nextSlide();
      } else {
        previousSlide();
      }
    } else {
      setIsMoving(false);
      setPointStart([]);
      setPointMove(0);
    }
  };

  const touchStart = e => {
    setPointStart([e.touches[0].clientX, e.touches[0].clientY]);
  };

  const touchMove = e => {
    // 移動距離(touch)
    let result = pointStart[0] - e.touches[0].clientX;
    // 計算移動方向，若移動方向為垂直則不執行carousel，若為水平則移動carousel
    // 並且不會隨著移動的斜率改變方向
    if (e.touches && direction === '') {
      const endY = e.touches[0].clientY,
        startY = pointStart[1],
        endX = e.touches[0].clientX,
        startX = pointStart[0];
      const m = (endY - startY) / (endX - startX);
      if (m < 1 && m > -1) {
        setIsMoving(true);
        setPointMove(result);
        setDirection('horizonal');
      } else {
        setDirection('vertical');
      }
    } else if (e.touches && direction === 'horizonal') {
      setPointMove(result);
    }
  };

  const touchEnd = () => {
    if (Math.abs(pointMove) > window.innerWidth / 5) {
      if (pointMove > 0) {
        nextSlide();
      } else {
        previousSlide();
      }
    } else {
      setIsMoving(false);
      setPointStart([]);
      setPointMove(0);
      setDirection('');
    }
  };

  const previousSlide = () => {
    let result = undefined;
    if (deviceSize === 'mobile') {
      result = index !== 0 ? index - 1 : 0;
    } else {
      result = index !== 0 ? index - 1 : index;
    }
    onSwipe(result);
    setIndex(result);
    setIsMoving(false);
    setPointStart([]);
    setPointMove(0);
    setDirection('');
  };

  const nextSlide = () => {
    let result = undefined;
    if (deviceSize === 'mobile') {
      result = index !== items.length - 1 ? index + 1 : index;
    } else {
      result = index !== Math.ceil(items.length / count) - 1 ? index + 1 : index;
    }
    onSwipe(result);
    setIndex(result);
    setIsMoving(false);
    setPointStart([]);
    setPointMove(0);
    setDirection('');
  };

  const itemWidth = useCallback(() => {
    let width;
    if (count === 1) {
      width = customWidth || window.innerWidth - 40;
    } else {
      if (fullWidth) {
        width = (window.innerWidth - 168) / count - 10;
      } else {
        width = (count === 3 ? fixedContainerWidth[0] : fixedContainerWidth[1]) / count - 10;
      }
    }
    return width;
  }, [count, fixedContainerWidth, fullWidth]);

  const ulOffset = useCallback(() => {
    let width = itemWidth() + 10;
    if (fullWidth) {
      let offset = (() => {
        if (window.innerWidth < breakpoint[1]) {
          return customWidth ? (window.innerWidth - customWidth - 10) / 2 : 15;
        } else {
          return 0;
        }
      })();
      let margin = 0;
      if (window.innerWidth > breakpoint[1]) {
        margin = 84;

        if (window.innerWidth <= 767) {
          margin = window.innerWidth - 600 / 2;
        }
        if (items.length === 1) {
          offset = window.innerWidth / 2 - width / 2 - margin;
        } else if (items.length === 2) {
          offset = window.innerWidth / 2 - width - margin;
        }
      }
      return -(index * width * count + pointMove) + offset;
    } else {
      if (count === 1) {
        return -(index * width + pointMove) + 15;
      } else {
        return -(index * width * count + pointMove);
      }
    }
  }, [breakpoint, count, pointMove, index, fullWidth, itemWidth, items.length]);

  //   moveing method --------^

  //   css width

  const carouselStyle = useCallback(() => {
    let width;
    if (fullWidth || count === 1) {
      width = '100%';
    } else {
      if (count === 3) {
        width = `${fixedContainerWidth[0] + 100}px`;
      } else if (count === 2) {
        width = `${fixedContainerWidth[1] + 100}px`;
      }
    }

    return { width };
  }, [count, fullWidth, fixedContainerWidth]);

  const carouselWrapperStyle = useCallback(() => {
    let width;
    if (fullWidth) return {};

    if (count === 1) {
      width = '100%';
    } else if (count === 2) {
      width = `${fixedContainerWidth[1]}px`;
    } else {
      width = `${fixedContainerWidth[0]}px`;
    }

    return { width };
  }, [count, fullWidth, fixedContainerWidth]);

  // item style

  const Arrow = () => {
    if (!arrow || count === 1) return '';

    const arrowPreviousClass = cx({
      arrow: true,
      prev: true,
      show: index !== 0,
      dark: darkMode,
    });

    const arrowNextClass = cx({
      arrow: true,
      next: true,
      show: index !== Math.ceil(items.length / count) - 1,
      dark: darkMode,
    });

    return (
      <>
        <div className={arrowPreviousClass} onClick={previousSlide}>
          <img src={darkMode ? iconPrevDark : iconPrev} alt="prev" />
        </div>
        <div className={arrowNextClass} onClick={nextSlide}>
          <img src={darkMode ? iconNextDark : iconNext} alt="next" />
        </div>
      </>
    );
  };

  const Indicators = () => {
    if (!indicator || (count === 1 && fullWidth)) return '';

    const itemsLength = count === 1 ? items.length : Math.ceil(items.length / count);
    let indicatorList = [];
    for (let i = 0; i < itemsLength; i++) {
      const indicatorClass = cx({
        indicator: true,
        active: i === index,
        dark: darkMode,
      });
      indicatorList.push(<div className={indicatorClass} key={i}></div>);
    }
    return <div className={styles['indicator-container']}>{indicatorList}</div>;
  };

  const carouselClass = cx({
    carousel: true,
    'full-width': fullWidth,
  });

  const content = () => {
    if (loading) {
      return (
        <div className={styles.loading}>
          <Spinners />
        </div>
      );
    }
    if (items.length === 0) {
      return '';
    }
    return (
      <ul
        className={styles['carousel-ul']}
        style={{
          transform: `translateX(${ulOffset()}px)`,
          transition: isMoving ? '' : 'all 0.2s ease 0s',
        }}
      >
        {items.map((item, i) => {
          return (
            <li
              key={i}
              className={styles['carousel-item']}
              style={{ flex: `0 0 ${itemWidth()}px` }}
              onTouchStart={touchStart}
              onTouchMove={touchMove}
              onTouchEnd={touchEnd}
              draggable
              onDragStart={dragStart}
              onDrag={dragMove}
              onDragEnd={dragEnd}
            >
              {item}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className={carouselClass} style={carouselStyle()}>
      {items.length !== 0 && <Arrow />}
      <div className={styles['carousel-wrapper']} style={carouselWrapperStyle()}>
        {content()}
      </div>
      <Indicators />
    </div>
  );
};

export default Carousel;
